import { useState, useEffect, useRef } from "react";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { MapPin, Clock, CreditCard, Heart, ArrowRight, Share2, Facebook, Twitter, Link as LinkIcon, Camera, Loader2, Map, ExternalLink, Box, Sun, Calendar, Users, Car, Image as ImageIcon, ShoppingBag, QrCode, Home } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

export default function TruthStand() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gbForm, setGbForm] = useState({ name: "", city_state: "", message: "", permission_to_display: true });
  const [gbSubmitting, setGbSubmitting] = useState(false);
  const [activeFaq, setActiveFaq] = useState(false);

  useEffect(() => {
    http.get("/truth-stand/public/data")
      .then(res => setData(res.data))
      .catch(e => {
        toast.error("The Truth Stand is currently unavailable.");
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Layout>
      <div className="min-h-screen bg-black flex items-center justify-center relative">
        <div className="absolute inset-0 opacity-[0.15] bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] pointer-events-none mix-blend-screen" />
        <div className="font-ui text-[#C9A055] tracking-[0.4em] text-xs uppercase animate-pulse">Loading Stand...</div>
      </div>
    </Layout>
  );

  if (!data || !data.settings?.enabled) return (
    <Layout>
      <div className="min-h-screen bg-black flex items-center justify-center relative">
         <div className="absolute inset-0 opacity-[0.15] bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] pointer-events-none mix-blend-screen" />
         <div className="font-display text-3xl md:text-5xl text-[#F4F1EA] tracking-wide relative z-10 text-center px-4">
           The Truth Stand is currently closed.
         </div>
      </div>
    </Layout>
  );

  const { settings, inventory, guestbook, analytics } = data;
  const isOpen = settings.status === "OPEN";

  const totalInventory = inventory?.reduce((acc, item) => acc + (item.quantity || 0), 0) || 0;

  const submitGb = async (e) => {
    e.preventDefault();
    if (!gbForm.message) { toast.error("Please leave a message."); return; }
    setGbSubmitting(true);
    try {
      await http.post("/truth-stand/public/guestbook", gbForm);
      toast.success("Thank you for signing the guestbook!");
      setGbForm({ name: "", city_state: "", message: "", permission_to_display: true });
    } catch {
      toast.error("Failed to submit.");
    } finally {
      setGbSubmitting(false);
    }
  };

  const share = async (kind) => {
    http.post("/truth-stand/public/track/share_clicks").catch(()=>{});
    const url = "https://MyArtStand.com";
    if (kind === "copy") {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
      return;
    }
    const text = encodeURIComponent(settings.share_text);
    const u = encodeURIComponent(url);
    const targets = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      twitter: `https://twitter.com/intent/tweet?url=${u}&text=${text}`
    };
    window.open(targets[kind], "_blank", "noopener,noreferrer,width=600,height=600");
  };

  const track = (event) => http.post(`/truth-stand/public/track/${event}`).catch(()=>{});

  // Derived stats
  const stats = [
    { icon: Users, num: (analytics?.page_views || 0) + (settings.base_stats?.views || 1204), label: "PEOPLE HAVE STOPPED BY", sub: "Thank you for being part of this journey." },
    { icon: Heart, num: (analytics?.buy_online_clicks || 0) + (settings.base_stats?.homes || 342), label: "TRUTHS HAVE FOUND HOMES", sub: "And thousands of lives have been touched." },
    { icon: Camera, num: (analytics?.guestbook_submissions || 0) + (settings.base_stats?.guestbooks || 89), label: "GUESTBOOK ENTRIES", sub: "See what visitors had to say." },
    { icon: MapPin, text: settings.location?.general || "JONESBOROUGH, TN", label: "LOCATION", sub: "Exact location shared when we're open." },
  ];

  return (
    <Layout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@1,400&family=Cormorant+Garamond:wght@400;600&display=swap');
        .font-cursive { font-family: 'Playfair Display', serif; font-style: italic; }
        .font-serif-display { font-family: 'Cormorant Garamond', serif; }
        
        .neon-box-open {
          border: 4px solid #fff;
          box-shadow: 0 0 20px rgba(255,255,255,0.8), inset 0 0 20px rgba(255,255,255,0.8);
          text-shadow: 0 0 15px rgba(255,255,255,0.8);
          border-radius: 20px;
        }
        .neon-box-closed {
          border: 4px solid #ff6b6b;
          box-shadow: 0 0 20px rgba(255,107,107,0.8), inset 0 0 20px rgba(255,107,107,0.8);
          text-shadow: 0 0 15px rgba(255,107,107,0.8);
          border-radius: 20px;
          color: #ff6b6b;
        }
        .bg-stand {
          background-image: url('${settings.hero_bg_image || "https://images.unsplash.com/photo-1593443821262-5625fcd78f49?auto=format&fit=crop&w=2000&q=80"}');
          background-size: cover;
          background-position: center;
          background-attachment: fixed;
        }
        .glass-panel {
          background: rgba(10, 10, 10, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .prose-rte p { margin-bottom: 1rem; }
        .prose-rte h1 { font-size: 2.5rem; margin-bottom: 1rem; color: #fff; font-family: 'Cormorant Garamond', serif; }
        .prose-rte h2 { font-size: 2rem; margin-bottom: 1rem; color: #fff; font-family: 'Cormorant Garamond', serif; }
        .prose-rte a { color: #C9A055; text-decoration: underline; text-underline-offset: 2px; }
      `}</style>

      {/* Global Background */}
      <div className="fixed inset-0 bg-stand z-[-2]"></div>
      <div className="fixed inset-0 bg-black/70 z-[-1]"></div>

      {/* Announcement Banner */}
      {settings.announcement_enabled && settings.announcement_banner && (
        <div className="bg-[#C9A055] text-black text-center py-2 px-4 font-ui text-[10px] tracking-[0.2em] uppercase font-bold sticky top-0 z-50">
          {settings.announcement_banner}
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-6 py-12 md:py-24">
        
        {/* HERO SECTION */}
        <section className="flex flex-col lg:flex-row items-center justify-between gap-16 mb-24">
          
          <motion.div initial="hidden" animate="visible" variants={fadeUp} className="flex-1 text-center lg:text-left">
            <h1 className="font-serif-display text-7xl md:text-8xl lg:text-[140px] leading-[0.85] text-[#F4F1EA] mb-8" dangerouslySetInnerHTML={{__html: (settings.hero_headline || "THE<br/>TRUTH<br/>STAND").replace(/\n/g, "<br/>")}} />
            <p className="font-ui text-[#C9A055] tracking-[0.2em] uppercase text-xs md:text-sm max-w-md mx-auto lg:mx-0 mb-8 leading-relaxed">
              {settings.hero_subheadline}
            </p>
            <p className="font-cursive text-4xl md:text-5xl text-[#F4F1EA]">
              Every Truth Has A Home.
            </p>
          </motion.div>

          <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ delay: 0.2 }} className="flex-1 flex flex-col items-center">
            <div className={`px-12 py-6 mb-8 ${isOpen ? 'neon-box-open text-white' : 'neon-box-closed'}`}>
              <h2 className="text-6xl md:text-8xl font-bold tracking-widest uppercase">
                {isOpen ? "OPEN" : "CLOSED"}
              </h2>
            </div>
            <div className="text-center space-y-2">
              <p className="font-ui text-white tracking-[0.3em] uppercase text-sm md:text-base">
                STOP BY ANYTIME.
              </p>
              <p className="font-ui text-[#A8A39D] tracking-[0.2em] uppercase text-xs md:text-sm">
                SELF-SERVE<br/>HONOR SYSTEM
              </p>
              <p className="font-ui text-[#A8A39D] tracking-[0.2em] uppercase text-xs pt-2 border-t border-white/20 mt-4">
                {settings.location?.general || "JONESBOROUGH, TENNESSEE"}
              </p>
            </div>
          </motion.div>
          
        </section>

        {/* STATUS BAR */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="glass-panel p-6 md:p-8 mb-24">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 divide-y md:divide-y-0 md:divide-x divide-white/10 text-center">
            <div className="px-4">
              <div className="flex justify-center items-center gap-2 mb-3">
                <div className={`w-2 h-2 rounded-full ${isOpen ? 'bg-green-500 shadow-[0_0_10px_#22c55e]' : 'bg-red-500 shadow-[0_0_10px_#ef4444]'}`}></div>
                <h3 className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#A8A39D]">STATUS</h3>
              </div>
              <p className="font-serif-display text-3xl text-white uppercase tracking-widest">{settings.status.replace("_", " ")}</p>
              <div className="font-body text-[#A8A39D] text-xs mt-2 prose-rte" dangerouslySetInnerHTML={{ __html: settings.status_message }} />
            </div>
            
            <div className="px-4 pt-6 md:pt-0">
              <Clock className="mx-auto mb-3 text-[#C9A055]" strokeWidth={1} size={20}/>
              <h3 className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#A8A39D] mb-2">LAST UPDATED</h3>
              <p className="font-body text-white text-sm">
                {new Date(settings.updated_at || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </p>
            </div>

            <div className="px-4 pt-6 md:pt-0">
              <Box className="mx-auto mb-3 text-[#C9A055]" strokeWidth={1} size={20}/>
              <h3 className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#A8A39D] mb-2">INVENTORY</h3>
              <p className="font-body text-white text-sm">{totalInventory} Truths<br/>Available</p>
            </div>

            <div className="px-4 pt-6 md:pt-0">
              <Sun className="mx-auto mb-3 text-[#C9A055]" strokeWidth={1} size={20}/>
              <h3 className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#A8A39D] mb-2">WEATHER</h3>
              <p className="font-body text-white text-sm">{settings.location?.weather_note || "Check local forecast"}</p>
            </div>

            <div className="px-4 pt-6 md:pt-0">
              <Calendar className="mx-auto mb-3 text-[#C9A055]" strokeWidth={1} size={20}/>
              <h3 className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#A8A39D] mb-2">NEXT RESTOCK</h3>
              <p className="font-body text-white text-sm">{settings.next_open_time || "Check back soon"}</p>
            </div>
          </div>
        </motion.section>

        {/* HOW IT WORKS */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-24">
          <div className="flex items-center gap-6 mb-16">
            <div className="flex-1 h-px bg-white/20"></div>
            <h2 className="font-ui text-[11px] tracking-[0.4em] uppercase text-[#C9A055]">HOW IT WORKS</h2>
            <div className="flex-1 h-px bg-white/20"></div>
          </div>

          <div className="flex flex-wrap md:flex-nowrap justify-center gap-8 md:gap-4 relative">
            <div className="hidden md:block absolute top-10 left-10 right-10 h-px bg-white/10 z-0"></div>
            
            {[
              { i: Car, t: "STOP BY", d: "Come when we're open." },
              { i: ImageIcon, t: "BROWSE", d: "Take your time. Read the stories." },
              { i: Heart, t: "CHOOSE", d: "Find the truth that speaks to you." },
              { i: CreditCard, t: "PAY", d: "Use cash, Venmo, Cash App, or card." },
              { i: ShoppingBag, t: "TAKE HOME", d: "Your new truth is ready for its home." },
              { i: QrCode, t: "REGISTER", d: "Scan the QR on your certificate." },
              { i: Home, t: "BELONG", d: "You're now part of the family." }
            ].map((step, idx) => {
              const Icon = step.i;
              return (
                <div key={idx} className="flex-1 min-w-[120px] text-center relative z-10">
                  <div className="w-20 h-20 mx-auto bg-[#0a0a0a] border border-white/20 rounded-full flex items-center justify-center mb-4">
                    <Icon className="text-white" strokeWidth={1} size={28}/>
                  </div>
                  <div className="font-serif-display text-2xl text-[#C9A055] mb-2">{idx + 1}</div>
                  <h3 className="font-ui text-[10px] tracking-[0.2em] uppercase text-white mb-2">{step.t}</h3>
                  <p className="font-body text-xs text-[#A8A39D]">{step.d}</p>
                </div>
              );
            })}
          </div>
        </motion.section>

        {/* FULL PAGE INVENTORY / DISCOVER SECTION */}
        <motion.section id="discover" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-24">
          <div className="flex items-center gap-6 mb-16">
            <div className="flex-1 h-px bg-white/20"></div>
            <h2 className="font-ui text-[11px] tracking-[0.4em] uppercase text-[#C9A055]">WHAT YOU'LL FIND</h2>
            <div className="flex-1 h-px bg-white/20"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {settings.what_youll_find?.map((item, i) => (
               <div key={i} className={`glass-panel p-6 ${item.available === false ? 'opacity-50' : ''}`}>
                 <h3 className="font-serif-display text-2xl text-white mb-2">{item.title}</h3>
                 <p className="font-body text-xs text-[#A8A39D] mb-6 min-h-[40px]">{item.desc}</p>
                 <div className="flex items-end justify-between mt-auto">
                    <span className="font-ui tracking-[0.2em] text-[#C9A055]">{item.price}</span>
                    {item.available === false && <span className="font-ui text-[9px] tracking-widest uppercase text-red-400">Unavailable</span>}
                 </div>
               </div>
            ))}
          </div>
        </motion.section>

        {/* PAYMENT OPTIONS - Inlined directly instead of modal */}
        <motion.section id="payment" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-24">
          <div className="flex items-center gap-6 mb-16">
            <div className="flex-1 h-px bg-white/20"></div>
            <h2 className="font-ui text-[11px] tracking-[0.4em] uppercase text-[#C9A055]">PAYMENT OPTIONS</h2>
            <div className="flex-1 h-px bg-white/20"></div>
          </div>
          
          <div className="text-center mb-12">
             <p className="font-serif-display text-3xl text-white mb-4">This stand runs entirely on trust.</p>
             <p className="font-body text-[#A8A39D]">Choose your truth, leave payment using one of the options below, and take it home.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
             <div className="glass-panel p-6 flex flex-col items-center text-center">
               <CreditCard className="text-[#C9A055] mb-4" size={32} strokeWidth={1.5}/>
               <h3 className="font-ui text-[11px] tracking-[0.2em] uppercase text-white mb-2">Credit Card</h3>
               <p className="font-body text-xs text-[#A8A39D] mb-6 flex-1">Secure checkout via Stripe</p>
               <StripePaymentCheckout />
             </div>
             
             <a href="https://venmo.com/u/MickeyTrivett" target="_blank" rel="noopener noreferrer" className="glass-panel p-6 flex flex-col items-center text-center hover:border-[#3D95CE] transition-colors group">
               <div className="w-8 h-8 flex items-center justify-center bg-[#3D95CE]/10 rounded-full mb-4 text-[#3D95CE]"><ExternalLink size={16}/></div>
               <h3 className="font-ui text-[11px] tracking-[0.2em] uppercase text-[#3D95CE] mb-2">Venmo</h3>
               <p className="font-body text-xs text-white mb-6 flex-1">@MickeyTrivett</p>
               <span className="font-ui text-[9px] tracking-widest text-[#A8A39D] group-hover:text-[#3D95CE]">PAY VIA VENMO</span>
             </a>

             <a href="https://cash.app/$MickeyTrivett" target="_blank" rel="noopener noreferrer" className="glass-panel p-6 flex flex-col items-center text-center hover:border-[#00D54B] transition-colors group">
               <div className="w-8 h-8 flex items-center justify-center bg-[#00D54B]/10 rounded-full mb-4 text-[#00D54B]"><ExternalLink size={16}/></div>
               <h3 className="font-ui text-[11px] tracking-[0.2em] uppercase text-[#00D54B] mb-2">Cash App</h3>
               <p className="font-body text-xs text-white mb-6 flex-1">$MickeyTrivett</p>
               <span className="font-ui text-[9px] tracking-widest text-[#A8A39D] group-hover:text-[#00D54B]">PAY VIA CASH APP</span>
             </a>
             
             {settings.payment_options?.filter(p => p.enabled && !['stripe', 'venmo', 'cash app'].includes(p.name.toLowerCase())).map((p, i) => (
                <a key={i} href={p.link || "#"} target="_blank" rel="noopener noreferrer" className="glass-panel p-6 flex flex-col items-center text-center hover:border-[#C9A055] transition-colors group">
                   <div className="w-8 h-8 flex items-center justify-center bg-[#C9A055]/10 rounded-full mb-4 text-[#C9A055]"><ExternalLink size={16}/></div>
                   <h3 className="font-ui text-[11px] tracking-[0.2em] uppercase text-[#C9A055] mb-2">{p.name}</h3>
                   <p className="font-body text-xs text-[#A8A39D] mb-6 flex-1">{p.instructions}</p>
                   <span className="font-ui text-[9px] tracking-widest text-[#A8A39D] group-hover:text-[#C9A055]">PAY VIA {p.name}</span>
                </a>
             ))}
          </div>
        </motion.section>

        {/* FAQs */}
        <motion.section id="faq" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-24 glass-panel p-8 md:p-12">
           <div className="text-center mb-12">
             <h2 className="font-serif-display text-4xl text-white mb-2">Questions?</h2>
             <p className="font-ui text-[#C9A055] tracking-[0.2em] text-[10px] uppercase">Common questions about the honor system</p>
           </div>
           <div className="space-y-6 max-w-3xl mx-auto">
             {settings.faqs?.length > 0 ? settings.faqs.map((faq, i) => (
               <div key={i} className="border-b border-white/10 pb-6 group cursor-pointer" onClick={() => setActiveFaq(activeFaq === i ? null : i)}>
                 <div className="flex justify-between items-center text-white group-hover:text-[#C9A055] transition-colors">
                   <h3 className="font-serif-display text-2xl">{faq.q}</h3>
                   <ChevronDown size={20} className={`transition-transform duration-300 ${activeFaq === i ? 'rotate-180 text-[#C9A055]' : ''}`}/>
                 </div>
                 <AnimatePresence>
                   {activeFaq === i && (
                     <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                       <p className="font-body text-[#A8A39D] mt-4 leading-relaxed">{faq.a}</p>
                     </motion.div>
                   )}
                 </AnimatePresence>
               </div>
             )) : (
               <div className="text-center text-[#A8A39D] py-12">No FAQs available.</div>
             )}
           </div>
        </motion.section>

        {/* SHARE & GUESTBOOK */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="grid md:grid-cols-2 gap-8 mb-24">
          <div className="glass-panel p-8 md:p-12 text-center flex flex-col justify-center">
            <Share2 className="mx-auto text-[#C9A055] mb-6" strokeWidth={1} size={40}/>
            <h3 className="font-serif-display text-4xl text-white mb-4">Share The Stand</h3>
            <p className="font-body text-[#A8A39D] mb-12 max-w-sm mx-auto leading-relaxed">{settings.share_text}</p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button onClick={() => share("facebook")} className="border border-white/20 px-6 py-3 font-ui text-[10px] tracking-[0.3em] uppercase text-white hover:bg-white hover:text-black transition-colors flex items-center justify-center gap-3"><Facebook size={16}/> Facebook</button>
              <button onClick={() => share("twitter")} className="border border-white/20 px-6 py-3 font-ui text-[10px] tracking-[0.3em] uppercase text-white hover:bg-white hover:text-black transition-colors flex items-center justify-center gap-3"><Twitter size={16}/> Twitter</button>
              <button onClick={() => share("copy")} className="border border-[#C9A055]/50 px-6 py-3 font-ui text-[10px] tracking-[0.3em] uppercase text-[#C9A055] hover:bg-[#C9A055] hover:text-black transition-colors flex items-center justify-center gap-3"><LinkIcon size={16}/> Copy Link</button>
            </div>
          </div>

          <div className="glass-panel p-8 md:p-12">
            <h3 className="font-serif-display text-4xl text-white mb-2 text-center">The Guestbook</h3>
            <p className="font-ui text-[#C9A055] tracking-[0.2em] text-[10px] uppercase text-center mb-8">Leave a note for the next visitor</p>
            
            <form onSubmit={submitGb} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input placeholder="Name (optional)" value={gbForm.name} onChange={e=>setGbForm({...gbForm, name: e.target.value})} className="bg-black/50 border border-white/20 text-white font-body px-4 py-3 w-full focus:border-[#C9A055] focus:outline-none"/>
                <input placeholder="City, State (optional)" value={gbForm.city_state} onChange={e=>setGbForm({...gbForm, city_state: e.target.value})} className="bg-black/50 border border-white/20 text-white font-body px-4 py-3 w-full focus:border-[#C9A055] focus:outline-none"/>
              </div>
              <textarea required placeholder="Your message..." rows={3} value={gbForm.message} onChange={e=>setGbForm({...gbForm, message: e.target.value})} className="bg-black/50 border border-white/20 text-white font-body px-4 py-3 w-full focus:border-[#C9A055] focus:outline-none resize-none"/>
              <button disabled={gbSubmitting} className="w-full border border-[#C9A055] bg-[#C9A055] text-black font-ui text-[10px] tracking-[0.3em] uppercase py-3 hover:bg-transparent hover:text-[#C9A055] transition-colors font-bold disabled:opacity-50">
                {gbSubmitting ? "Submitting..." : "Sign Guestbook"}
              </button>
            </form>

            {guestbook?.length > 0 && (
              <div className="mt-8 space-y-4 max-h-[200px] overflow-y-auto pr-4 custom-scrollbar">
                {guestbook.map(g => (
                  <div key={g.id} className="border-b border-white/10 pb-4">
                    <div className="font-cursive text-lg text-[#F4F1EA] mb-2">"{g.message}"</div>
                    <div className="font-ui text-[9px] tracking-[0.2em] text-[#C9A055] uppercase">{g.name || "Anonymous"} <span className="text-[#8a857d]">{g.city_state}</span></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.section>

        {/* BOTTOM STATS */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="border-t border-white/20 pt-12 pb-12 glass-panel mt-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-white/10">
            {stats.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="px-4">
                  <div className="flex justify-center items-center gap-3 mb-4 text-[#F4F1EA]">
                    <Icon strokeWidth={1} size={24}/>
                    <span className="font-serif-display text-4xl">{s.num !== undefined ? s.num.toLocaleString() : s.text}</span>
                  </div>
                  <h3 className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#C9A055] mb-2">{s.label}</h3>
                  <p className="font-body text-xs text-[#A8A39D]">{s.sub}</p>
                </div>
              )
            })}
          </div>
          <div className="text-center mt-16 pt-8 border-t border-white/10">
            <p className="font-ui text-[11px] tracking-[0.3em] uppercase text-[#F4F1EA] mb-2">"WHICH TRUTH DO YOU NEED TODAY?"</p>
            <p className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#C9A055]">WE'LL SEE YOU AT THE STAND.</p>
          </div>
        </motion.section>

      </div>
    </Layout>
  );
}

function StripePaymentCheckout() {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    const num = parseFloat(amount);
    if (isNaN(num) || num < 1) {
      toast.error("Please enter an amount of at least $1.00");
      return;
    }
    setLoading(true);
    try {
      const { data } = await http.post("/truth-stand/public/checkout", {
        amount: num,
        description: "The Truth Stand - Honor System Payment"
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Checkout failed. " + (e.response?.data?.detail || e.message));
      setLoading(false);
    }
  };

  return (
    <div className="w-full mt-auto">
      <div className="relative mb-3">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a857d]">$</span>
        <input 
          type="number" 
          placeholder="0.00" 
          value={amount} 
          onChange={e => setAmount(e.target.value)} 
          className="w-full bg-black/50 border border-white/20 text-white pl-7 pr-3 py-2 text-sm focus:border-[#C9A055] focus:outline-none transition-colors"
        />
      </div>
      <button 
        onClick={handleCheckout} 
        disabled={loading}
        className="w-full border border-[#C9A055]/50 bg-[#C9A055]/10 text-[#C9A055] px-4 py-2 text-[10px] uppercase tracking-widest hover:bg-[#C9A055] hover:text-black transition-colors disabled:opacity-50"
      >
        {loading ? "..." : "PAY ONLINE"}
      </button>
    </div>
  );
}
